import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FileText, Sparkles, Trash2, Upload } from 'lucide-react'
import { apiErrorText } from '@/lib/api-error'
import { bestaetige } from '@/lib/dialoge'
import { cardTitle, formCardSm, inlineInput } from '@/lib/form-styles'
import { FeldStatus, FeldStatusZeichen, useFeld, type Speicherstelle } from '@/lib/speichern'
import { deleteCalculationFile, fetchCalculationFiles, oeffneKalkulationsdatei } from '../api/calculations.api'
import { fetchRaster } from '../api/calculation-positions.api'
import { fetchSheet } from '../api/calculation-sheet.api'
import { WAEHLBARE_DATEIARTEN, DATEIART_ANZEIGE, datumText } from '../lib/kalkulation-labels'
import { useZeilenStelle } from '../lib/kalkulation-einstellungen'
import type { CalcFile, CalcPosition, Calculation } from '../types/calculation.types'
import { dateiGroesse, haengeAn } from './KalkulationsDateien'

const ARTEN = WAEHLBARE_DATEIARTEN

type Filter = 'alle' | 'zugeordnet' | 'frei'

/** Eine Position in der Auswahl „gehört zu": Nummer und Kurztext. */
const positionText = (p: Pick<CalcPosition, 'displayNumber' | 'shortText'>) => `${p.displayNumber} ${p.shortText}`.trim()

/** Materialzeilen je Position, einmal geladen — mehrere Dateien an derselben Position fragen nur einmal. */
type ZeilenCache = Map<string, Promise<{ id: string; name: string }[]>>

/**
 * AP-37 4.18.1 — **das Kalkulationsverzeichnis** (Reiter Dokumente): Dateien
 * mit Dateiart (beim Ablegen aus dem Namen vorgeschlagen), „nicht an die KI",
 * gelesen am. Ein Plan geht nie an die KI (12.2-24) — das Kennzeichen ist dort
 * gesetzt und gesperrt. Das Vorschlagen aus Unterlagen kommt mit Phase N.
 *
 * Nachtrag 3 (Detailblatt O 4a.1): Spalte **gehört zu** — eine Datei lässt sich
 * einer Position oder einer ihrer Materialzeilen zuordnen und wieder lösen; sie
 * bleibt dabei im Verzeichnis. Beim Ablegen gleich an eine Position.
 */
export function CalculationFilesTab({
  kalkulation,
  onChanged,
  kiLesen,
  listeLesen,
}: {
  kalkulation: Calculation
  onChanged: () => void
  /** Detailblatt N 15. (N1): „Preise lesen" je Lieferantenangebot — nur mit `.AI`, Recht und offener Fassung */
  kiLesen?: (datei: CalcFile) => void
  /** N2: „Material übernehmen" je Stückliste (Excel/CSV ohne KI, PDF über den eingebauten Weg) */
  listeLesen?: (datei: CalcFile) => void
}) {
  const [dateien, setDateien] = useState<CalcFile[]>([])
  const [positionen, setPositionen] = useState<CalcPosition[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [laedt, setLaedt] = useState(false)
  const [filter, setFilter] = useState<Filter>('alle')
  const [ablageAn, setAblageAn] = useState('')
  const eingabe = useRef<HTMLInputElement>(null)
  const zeilenCache = useRef<ZeilenCache>(new Map())
  const darf = kalkulation.writable

  const laden = useCallback(() => {
    fetchCalculationFiles(kalkulation.id)
      .then(setDateien)
      .catch((e) => setFehler(apiErrorText(e, 'Die Dokumente ließen sich nicht laden.')))
  }, [kalkulation.id])
  useEffect(laden, [laden])
  useEffect(() => {
    fetchRaster(kalkulation.id)
      .then((r) => setPositionen(r.positions))
      .catch(() => setPositionen([]))
  }, [kalkulation.id])

  const zeilenVon = useCallback(
    (positionId: string) => {
      const cache = zeilenCache.current
      if (!cache.has(positionId)) {
        cache.set(
          positionId,
          fetchSheet(kalkulation.id, positionId)
            // Menge und Einheit davor — zwei Zeilen desselben Artikels hießen sonst gleich.
            .then((b) => b.costLines.map((z) => ({ id: z.id, name: `${z.quantity.toLocaleString('de-DE', { maximumFractionDigits: 3 })} ${z.unitCode ?? ''} ${z.name}`.replace(/\s+/g, ' ') })))
            .catch(() => []),
        )
      }
      return cache.get(positionId)!
    },
    [kalkulation.id],
  )

  const sichtbar = useMemo(
    () => dateien.filter((d) => (filter === 'zugeordnet' ? d.positionId !== null : filter === 'frei' ? d.positionId === null : true)),
    [dateien, filter],
  )

  const ablegen = async (liste: FileList | null) => {
    if (!liste?.length) return
    setFehler(null)
    setLaedt(true)
    try {
      const r = await haengeAn(kalkulation.id, Array.from(liste), ablageAn ? { positionId: ablageAn } : {})
      if (r.fehler.length) setFehler(r.fehler.join(' · '))
      if (r.ok) {
        laden()
        onChanged()
      }
    } finally {
      setLaedt(false)
      if (eingabe.current) eingabe.current.value = ''
    }
  }

  return (
    <div
      className={formCardSm}
      onDragOver={(e) => darf && e.preventDefault()}
      onDrop={(e) => {
        if (!darf) return
        e.preventDefault()
        void ablegen(e.dataTransfer.files)
      }}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className={cardTitle}>Kalkulationsverzeichnis</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Lieferantenangebote, Stücklisten, Anfragen und Pläne dieser Kalkulation. Eine neue Fassung zeigt auf dieselben Dateien. Ein LV im
            Normformat gehört in den Reiter LV. Dateien an einer Position oder Materialzeile stehen auch dort.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select className="rounded-md border border-border bg-background px-1.5 py-1.5 text-sm" aria-label="Filter" value={filter} onChange={(e) => setFilter(e.target.value as Filter)}>
            <option value="alle">alle Dateien</option>
            <option value="zugeordnet">an Positionen</option>
            <option value="frei">ohne Zuordnung</option>
          </select>
          {darf && (
            <>
              <select className="max-w-56 rounded-md border border-border bg-background px-1.5 py-1.5 text-sm" aria-label="Ablegen an" value={ablageAn} onChange={(e) => setAblageAn(e.target.value)}>
                <option value="">ins Verzeichnis</option>
                {positionen.map((p) => (
                  <option key={p.id} value={p.id}>
                    an Pos. {positionText(p)}
                  </option>
                ))}
              </select>
              <input ref={eingabe} type="file" multiple className="hidden" onChange={(e) => void ablegen(e.target.files)} />
              <button type="button" className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm" disabled={laedt} onClick={() => eingabe.current?.click()}>
                <Upload className="h-4 w-4" /> {laedt ? 'Lädt hoch …' : 'Dateien ablegen'}
              </button>
            </>
          )}
        </div>
      </div>
      {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
      {/* Schmal (Assistent offen): waagrecht rollen statt die Dateinamen Zeichen für Zeichen umzubrechen. */}
      <div className="mt-3 overflow-x-auto">
      <table className="w-full min-w-[56rem] text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="py-1.5 pr-2">Datei</th>
            <th className="py-1.5 pr-2">gehört zu</th>
            <th className="py-1.5 pr-2">Dateiart</th>
            <th className="py-1.5 pr-2">nicht an die KI</th>
            <th className="py-1.5 pr-2">gelesen am</th>
            <th className="py-1.5 pr-2">abgelegt</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {sichtbar.length === 0 && (
            <tr>
              <td colSpan={7} className="py-6 text-center text-muted-foreground">
                {dateien.length > 0 ? 'Keine Datei passt zum Filter.' : darf ? 'Noch keine Dokumente — Dateien hierher ziehen oder „Dateien ablegen".' : 'Keine Dokumente.'}
              </td>
            </tr>
          )}
          {sichtbar.map((d) => (
            <DateiZeile
              key={d.id}
              kalkulationId={kalkulation.id}
              datei={d}
              darf={darf}
              positionen={positionen}
              zeilenVon={zeilenVon}
              kiLesen={kiLesen}
              listeLesen={listeLesen}
              onLoeschen={async () => {
                if (!(await bestaetige(`„${d.file.originalName}" entfernen?`, { ok: 'Entfernen', gefaehrlich: true }))) return
                try {
                  await deleteCalculationFile(kalkulation.id, d.id)
                  laden()
                  onChanged()
                } catch (e) {
                  setFehler(apiErrorText(e, 'Die Datei ließ sich nicht entfernen.'))
                }
              }}
            />
          ))}
        </tbody>
      </table>
      </div>
    </div>
  )
}

function DateiZeile({
  kalkulationId,
  datei: d,
  darf,
  positionen,
  zeilenVon,
  kiLesen,
  listeLesen,
  onLoeschen,
}: {
  kalkulationId: string
  datei: CalcFile
  darf: boolean
  positionen: CalcPosition[]
  zeilenVon: (positionId: string) => Promise<{ id: string; name: string }[]>
  kiLesen?: (datei: CalcFile) => void
  listeLesen?: (datei: CalcFile) => void
  onLoeschen: () => void
}) {
  const stelle = useZeilenStelle('calcFile', `/calculations/${kalkulationId}/files/${d.id}`, d, d.file.originalName) as Speicherstelle<unknown>
  // Detailblatt K 7.2: Eine Ausgabe ist der Nachweis der Abgabe — keine andere Art, keine Zuordnung, nicht löschbar.
  const ausgabe = d.kind === 'OUTPUT'
  const art = useFeld<string>(stelle, 'kind', { art: 'auswahl', label: 'Dateiart', gesperrt: !darf || ausgabe })
  const ohneKi = useFeld<boolean>(stelle, 'excludeFromAi', { art: 'schalter', label: 'nicht an die KI', gesperrt: !darf || art.wert === 'DRAWING' })
  // Eine andere Position löst die Materialzeile gleich mit (Folgefeld).
  const position = useFeld<string | null>(stelle, 'positionId', { art: 'auswahl', label: 'gehört zu', gesperrt: !darf || ausgabe, folge: () => ({ costLineId: null }) })
  const zeile = useFeld<string | null>(stelle, 'costLineId', { art: 'auswahl', label: 'Materialzeile', gesperrt: !darf || !position.wert })
  const [zeilen, setZeilen] = useState<{ id: string; name: string }[]>([])

  useEffect(() => {
    if (!position.wert) return setZeilen([])
    let aktiv = true
    void zeilenVon(position.wert).then((l) => aktiv && setZeilen(l))
    return () => {
      aktiv = false
    }
  }, [position.wert, zeilenVon])

  // Eine Position im Papierkorb steht nicht in der Auswahl — die Zuordnung bleibt trotzdem sichtbar.
  const imPapierkorb = d.position && !positionen.some((p) => p.id === d.position!.id)

  return (
    <tr className="border-b border-border/60 align-top">
      <td className="py-1.5 pr-2">
        <button type="button" className="inline-flex items-center gap-1.5 text-left text-primary hover:underline" onClick={() => void oeffneKalkulationsdatei(kalkulationId, d.id, d.file.originalName)}>
          <FileText className="h-4 w-4 shrink-0" />
          <span className="break-all">{d.file.originalName}</span>
        </button>
        <span className="block text-xs text-muted-foreground">{dateiGroesse(d.file.sizeBytes)}</span>
      </td>
      <td className="py-1.5 pr-2">
        <span className="flex items-center gap-1">
          <select className={`${inlineInput} !w-52`} aria-label="gehört zu" {...position.props}>
            <option value="">— ohne Zuordnung —</option>
            {imPapierkorb && d.position && (
              <option value={d.position.id}>
                {d.position.oz ? `${d.position.oz} ` : ''}
                {d.position.shortText} (Papierkorb)
              </option>
            )}
            {positionen.map((p) => (
              <option key={p.id} value={p.id}>
                Pos. {positionText(p)}
              </option>
            ))}
          </select>
          <FeldStatusZeichen status={position} />
        </span>
        {position.wert && (
          <span className="mt-1 flex items-center gap-1">
            <select className={`${inlineInput} !w-52`} aria-label="Materialzeile" {...zeile.props}>
              <option value="">an der Position</option>
              {d.costLine && !zeilen.some((z) => z.id === d.costLine!.id) && <option value={d.costLine.id}>{d.costLine.name}</option>}
              {zeilen.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name}
                </option>
              ))}
            </select>
            <FeldStatusZeichen status={zeile} />
          </span>
        )}
        <FeldStatus status={position} zeichen={false} />
        <FeldStatus status={zeile} zeichen={false} />
      </td>
      <td className="py-1.5 pr-2">
        <span className="flex items-center gap-1">
          <select className={`${inlineInput} !w-56`} aria-label="Dateiart" {...art.props}>
            {(ausgabe ? (['OUTPUT'] as const) : ARTEN).map((a) => (
              <option key={a} value={a}>
                {DATEIART_ANZEIGE[a]}
              </option>
            ))}
          </select>
          <FeldStatusZeichen status={art} />
        </span>
        <FeldStatus status={art} zeichen={false} />
      </td>
      <td className="py-1.5 pr-2">
        <span className="inline-flex items-center gap-1">
          <input type="checkbox" {...ohneKi.schalter} />
          <FeldStatusZeichen status={ohneKi} />
        </span>
      </td>
      <td className="py-1.5 pr-2 text-muted-foreground">
        {d.aiReadAt ? datumText(d.aiReadAt, true) : '—'}
        {kiLesen && art.wert === 'SUPPLIER_OFFER' && !ohneKi.wert && d.file.mimeType === 'application/pdf' && (
          <button type="button" className="mt-1 flex items-center gap-1 text-xs text-primary hover:underline" onClick={() => kiLesen(d)}>
            <Sparkles className="h-3.5 w-3.5" /> Preise lesen …
          </button>
        )}
        {/* N2: Excel/CSV ohne KI, PDF mit KI (dann nur ohne „nicht an die KI"). */}
        {listeLesen && art.wert === 'MATERIAL_LIST' && (d.file.mimeType !== 'application/pdf' || !ohneKi.wert) && /\.(xlsx|csv|pdf)$/i.test(d.file.originalName) && (
          <button type="button" className="mt-1 flex items-center gap-1 text-xs text-primary hover:underline" onClick={() => listeLesen(d)}>
            <Sparkles className="h-3.5 w-3.5" /> Material übernehmen …
          </button>
        )}
      </td>
      <td className="py-1.5 pr-2 text-muted-foreground">
        {datumText(d.uploadedAt, true)}
        {d.uploadedByName && <span className="block text-xs">{d.uploadedByName}</span>}
      </td>
      <td className="py-1.5 text-right">
        {darf && !ausgabe && (
          <button type="button" className="text-muted-foreground hover:text-destructive" title="entfernen" onClick={onLoeschen}>
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </td>
    </tr>
  )
}
